import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthRedirectService } from '../auth/auth-redirect.service';
import { GetterService } from './getter-service.service';
import { RequestService } from './request.service';
import { PosthogService } from '../analytics/services/posthog.service';
import { brandFlavor as currentBrandFlavor } from 'src/app/brand/brand-context';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private isLoggedInSub = new BehaviorSubject<boolean>(false);
  isLoggedIn$: Observable<boolean> = this.isLoggedInSub.asObservable();

  constructor(
    private _local: GetterService,
    private _requestService: RequestService,
    private _router: Router,
    private analytics: PosthogService,
    private authRedirectService: AuthRedirectService
  ) {
    if (this._local.get('access_token')) this.loggedIn();
  }

  loggedIn() {
    this.isLoggedInSub.next(true);
  }

  /**
   * Re-reads the signed-in customer's profile into brand storage and
   * identifies them to analytics. Runs on boot (AppComponent) and after a
   * token refresh. Moved verbatim from the removed legacy
   * login/login.service.ts (Oct 2026); AuthComponent.getUserData still does
   * its own fetch at sign-in time.
   */
  refreshCustomerProfile() {
    const username = this._local.get('username');
    if (!username) return;
    const brandFlavor = currentBrandFlavor();

    this._requestService
      .postRequest('api/Customer/getCustomer/' + username + '/0', {
        BrandFlavor: brandFlavor,
      })
      .subscribe({
        next: (res) => {
          if (res['Info']['Status'] != 200) return;
          const data = res['Data'];

          this.analytics.captureEvent('User Logged In', {
            CustomerId: data['CustomerId'],
            CustomerEmail: data['CustomerEmail'],
            BrandFlavor: brandFlavor,
          });
          this.analytics.identifyUser(data['CustomerId'], {
            email: data['CustomerEmail'],
            firstName: data['CustomerFirstName'],
            lastName: data['CustomerLastName'],
            brandFlavor: brandFlavor,
          });

          this._local.set('CustomerId', data['CustomerId']);
          this._local.set(btoa('CustomerEmail'), btoa(data['CustomerEmail']));
          this._local.set(
            btoa('CustomerPassword'),
            btoa(data['CustomerPassword'])
          );
          this._local.set('CustomerFirstName', data['CustomerFirstName']);
          this._local.set('CustomerLastName', data['CustomerLastName']);
          this._local.set('CustomerMobile', data['CustomerMobile']);
          this._local.set('CustomerImageUrl', data['CustomerImageUrl']);
          this._local.set('FK_BrandId_Customer', data['FK_BrandId_Customer']);
          this._local.set('PromoCode', data['PromoCode']);
        },
        error: () => {},
      });
  }

  logout(redirectToLogin = true) {
    this._local.remove('cartProductItems');
    this._local.remove('cartRecipeItems');
    this._requestService.updateCartCounter();
    this.isLoggedInSub.next(false);
    this.removeSession(redirectToLogin);
  }

  createSession(userName: string, accessToken: any, refreshToken: any) {
    this._local.set('username', userName);
    this._local.set('access_token', accessToken);
    this._local.set('refresh_token', refreshToken);
  }

  private removeSession(redirectToLogin = true) {
    this._local.remove('username');
    this._local.remove('CustomerId');
    this._local.remove('CustomerFirstName');
    this._local.remove('CustomerLastName');
    this._local.remove('CustomerImageUrl');
    this._local.remove('access_token');
    this._local.remove('refresh_token');
    this._local.remove('PromoCode');
    this._local.removeSessionItem('access_token');

    if (redirectToLogin) {
      this.authRedirectService.capture(this._router.url);
      this._router.navigate([this._requestService.setUrl('login')]);
    }
  }

  refreshToken() {
    let params = new HttpParams();
    params = params.append('grant_type', 'refresh_token');
    params = params.append('refresh_token', this._local.get('refresh_token'));
    this._requestService.postRequest('Token', params).subscribe({
      next: (response) => {
        this.createSession(
          this._local.get('username'),
          response['access_token'],
          response['refresh_token']
        );
        this.isLoggedInSub.next(true);

        this.refreshCustomerProfile();
      },
      error: (error) => {
        this.logout();
      },
    });
  }

}
